import { z } from 'zod';
import { ToolResultSchema } from '../tools/sharefox.js';
import { LockedContractSchema } from './contract.js';
// ── Intent Classification ──────────────────────────────────────────────────
// Phase 1 canonical intent names — from architecture doc §5.3 classifyIntent node.
// These align with the LangGraph orchestrator's intent routing tables and tool mappings.
export const IntentTypeSchema = z.enum([
    'general_info', // opening hours, policies, directions, contact info
    'product_recommendation', // which product fits the customer's needs (calls getProducts)
    'availability_check', // is a specific product available for given dates (calls getProductAvailability)
    'pricing_query', // how much does it cost (calls getProductAvailability / getProducts)
    'order_confirmation', // confirm/look up an existing order (calls getOrder)
    'cancellation_request', // customer wants to cancel a booking (RAG policy retrieval)
    'out_of_scope', // complaints, legal threats, payment disputes
    'unknown', // no clear signal — produce clarification draft
]);
// Human-readable label for each intent — single source of truth used by both
// the Outlook task pane (StatusBadge) and the Gmail card builder.
export const INTENT_LABELS = {
    general_info: 'General Info',
    product_recommendation: 'Product Recommendation',
    availability_check: 'Availability Check',
    pricing_query: 'Pricing Query',
    order_confirmation: 'Order Confirmation',
    cancellation_request: 'Cancellation',
    out_of_scope: 'Out of Scope',
    unknown: 'Unknown',
};
// Shared merchant context for the PoC. Replace with per-merchant config in production.
export const POC_MERCHANT_ID = '11111111-1111-1111-1111-111111111111';
// ── Streaming SSE Event Shapes ─────────────────────────────────────────────
export const SSEEventTypeSchema = z.enum([
    'intent_classified',
    'tool_call_start',
    'tool_call_result',
    'stream_chunk',
    'stream_end',
    'error',
]);
export const SSEIntentEventSchema = z.object({
    type: z.literal('intent_classified'),
    intent: IntentTypeSchema,
    confidence: z.number().min(0).max(1),
});
export const SSEToolCallStartEventSchema = z.object({
    type: z.literal('tool_call_start'),
    toolName: z.string(),
    message: z.string(),
});
export const SSEToolCallResultEventSchema = z.object({
    type: z.literal('tool_call_result'),
    toolName: z.string(),
    success: z.boolean(),
    summary: z.string(),
});
export const SSEChunkEventSchema = z.object({
    type: z.literal('stream_chunk'),
    delta: z.string(),
});
export const SSEEndEventSchema = z.object({
    type: z.literal('stream_end'),
    fullText: z.string(),
    contract: LockedContractSchema.optional(),
});
export const SSEErrorEventSchema = z.object({
    type: z.literal('error'),
    message: z.string(),
    code: z.string().optional(),
});
// Discriminated union validator. Returns the parsed event or null for sentinel/malformed lines.
// Centralises the JSON.parse + type-narrowing that both the Outlook SSE hook and the Gmail
// server previously duplicated with untyped JSON.parse casts.
const SSEEventSchema = z.discriminatedUnion('type', [
    SSEIntentEventSchema,
    SSEToolCallStartEventSchema,
    SSEToolCallResultEventSchema,
    SSEChunkEventSchema,
    SSEEndEventSchema,
    SSEErrorEventSchema,
]);
export function parseSSELine(line) {
    if (!line.startsWith('data: '))
        return null;
    const payload = line.slice(6).trim();
    if (payload === '[DONE]' || payload === '')
        return null;
    const result = SSEEventSchema.safeParse(JSON.parse(payload));
    return result.success ? result.data : null;
}
// ── Gmail Autocomplete ─────────────────────────────────────────────────────
export const AutocompleteSuggestionSchema = z.object({
    label: z.string(),
    value: z.string(),
    category: IntentTypeSchema.optional(),
});
export const AutocompleteResponseSchema = z.object({
    suggestions: z.array(AutocompleteSuggestionSchema),
});
// ── Orchestration Request ──────────────────────────────────────────────────
export const OrchestrationRequestSchema = z.object({
    emailBody: z.string().min(1),
    emailSubject: z.string().optional(),
    senderEmail: z.string().email().optional(),
    /** Inbound From display name — used for greeting; never invent from body text. */
    senderName: z.string().max(200).optional(),
    merchantId: z.string().uuid(),
    sessionId: z.string().optional(),
    platform: z.enum(['outlook', 'gmail']),
    /**
     * Channel — enables the AI Gateway contract to be channel-agnostic.
     * Phase 1 ships 'email' only; 'chat' and 'voice' are Phase 2/3 extension points.
     * The orchestrator echoes this back in V1DraftResponse so the add-in knows the context.
     */
    channel: z.enum(['email', 'chat', 'voice']).default('email'),
    /**
     * Thread/conversation ID from the email client.
     * Gmail: threadId. Outlook: conversationId.
     * Used by the orchestrator for conversation memory (Phase 1 seam, Phase 2 full support).
     */
    threadId: z.string().optional(),
    /**
     * Structured user context — replaces the legacy additionalInstructions prompt-injection vector.
     * The orchestrator treats this as data, not a prompt override.
     * Example: "Customer requested 10% loyalty discount" or "Use formal Norwegian tone".
     */
    userContext: z.string().max(500).optional(),
    /** @deprecated Use userContext instead. Kept for backward compat with existing add-in code. */
    extraInstructions: z.string().max(500).optional(),
    /**
     * Stable email/thread key from the host.
     * Gmail uses a thread-key value; Outlook uses the selected message item id.
     * The backend stores this as email_message_id for idempotency, recovery, and
     * prior-draft history lookups.
     */
    emailMessageId: z.string().optional(),
    /** When the customer email was received/sent, as an ISO timestamp. */
    emailReceivedAt: z.string().datetime({ offset: true }).optional(),
    /** Start a fresh run even if the same emailMessageId already has prior runs. */
    forceNew: z.boolean().optional(),
});
// ── V1 Draft API ───────────────────────────────────────────────────────────
export const ConfidenceBadgeSchema = z.enum(['high', 'medium', 'low']);
export const DraftCitationSchema = z.object({
    id: z.number(),
    source: z.string(),
    text: z.string(),
    /**
     * Source kind. 'knowledge' = RAG knowledge entry that cleared the display
     * similarity bar; 'knowledge_low_confidence' = RAG entry that fed the draft
     * but didn't clear it (rendered as a summary note, not a detailed citation);
     * 'live' = Sharefox Live API tool call used during drafting. Absent on
     * legacy runs → treat as 'knowledge'.
     */
    kind: z.enum(['knowledge', 'knowledge_low_confidence', 'live']).optional(),
    /**
     * Ready-made Sharefox Admin deep link for editing the knowledge entry behind
     * this citation ("Update knowledge"). Knowledge citations only; built by the
     * backend (which knows the tenant admin domain), never by the add-ins.
     */
    edit_url: z.string().optional(),
});
export const ToolFailureSchema = z.object({
    tool: z.string(),
    reason: z.string(),
});
export const DraftCandidateSchema = z.object({
    intent: IntentTypeSchema,
    confidence: z.number().min(0).max(1),
});
export const DraftAlternativeSchema = z.object({
    label: z.string(),
    draft: z.string(),
});
export const V1DraftResponseSchema = z.object({
    draft: z.string(),
    citations: z.array(DraftCitationSchema),
    /**
     * Ready-made Sharefox Admin deep link for adding a new knowledge entry
     * ("+ Add Knowledge"). Same admin host as citation `edit_url` but without an
     * entryId — built by the backend (which knows the tenant admin domain),
     * never by the add-ins. Null when the tenant has no known admin site (e.g.
     * not yet activated through Sharefox Admin).
     */
    knowledge_base_url: z.string().nullable().optional(),
    confidence: z.number().min(0).max(1),
    intent: IntentTypeSchema,
    entities: z.record(z.string(), z.string()),
    status: z.enum(['success', 'missing_entity', 'tool_failure', 'out_of_scope', 'low_confidence']),
    kind: z.enum(['full', 'clarification', 'escalation']),
    trace_id: z.string(),
    dimensions: z.array(z.unknown()),
    alternatives: z.array(DraftAlternativeSchema),
    missing_entities: z.array(z.string()),
    tool_failure: ToolFailureSchema.nullable(),
    badge: ConfidenceBadgeSchema,
    candidates: z.array(DraftCandidateSchema),
    speculative_alternative: z.object({ draft: z.string(), intent: z.string() }).nullable(),
    /**
     * Structured results of every Sharefox tool called during draft generation.
     * Each element records the tool name, the input used, and the typed output.
     * The add-ins render this as citations ("Based on: Sharefox Availability — NOK 1 875/day").
     * In production, the LangGraph orchestrator populates this from actual API calls.
     * In the mock, sharefoxTools.ts returns fixture-backed results.
     */
    tool_results: z.array(ToolResultSchema).default([]),
    /** Persisted merchant feedback for this draft history row, when present. */
    draft_feedback: z.enum(['like', 'dislike']).nullable().optional(),
    /**
     * Channel that produced this draft. Always 'email' in Phase 1.
     * Carried through so Phase 2/3 chat and voice surfaces can reuse the same
     * AI Gateway contract without adding new fields.
     */
    channel: z.enum(['email', 'chat', 'voice']).default('email'),
});
export const V1DraftRequestSchema = OrchestrationRequestSchema.extend({
    scenario: z
        .enum([
        'confidence_high',
        'confidence_medium',
        'confidence_low',
        'missing_entity',
        'out_of_scope',
        'tool_failure',
    ])
        .optional(),
});
export const V1RetryRequestSchema = V1DraftRequestSchema.extend({
    entity_key: z.string(),
    entity_value: z.string(),
});
export const V1RegenerateRequestSchema = V1DraftRequestSchema.extend({
    notes: z.string().max(500).optional(),
    regenerate_count: z.number().int().min(1),
    trace_id: z.string(),
});
export const V1FeedbackRequestSchema = z.object({
    trace_id: z.string(),
    action: z.enum(['sent', 'liked', 'disliked', 'regenerated', 'discarded', 'commented']),
    notes: z.string().optional(),
});
export const V1EscalateResponseSchema = z.object({
    escalation_alias: z.string().email(),
});
//# sourceMappingURL=api.js.map